import { useNavigate } from '@tanstack/react-router';
import { EllipsisVertical, FlagTriangleRight, X } from 'lucide-react';
import { memo, useState } from 'react';
import { toast } from 'sonner';

import { useRemoveDuo } from '@/features/duos/hooks/useRemoveDuo';
import { ReportModal } from '@/features/duos/modals/reportModal';
import { Button } from '@/shared/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu';
import { getErrorMessage } from '@/shared/utils/errorMessage';

export interface ReportProfileActionsProps {
  userId: string;
  triggerClassName?: string;
  isDuo?: boolean;
}

export const ReportProfileActions = memo(function ReportProfileActions(
  props: ReportProfileActionsProps,
) {
  const { userId, triggerClassName, isDuo } = props;
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const navigate = useNavigate();
  const removeDuoMutation = useRemoveDuo({
    onSuccess: () => {
      toast.success('Duo removed successfully');
      void navigate({ to: '/my-duos' });
    },
    onError: async error => {
      const message = await getErrorMessage(error);
      toast.error(message);
    },
  });

  const handleReportClick = () => {
    setIsReportModalOpen(true);
  };

  const handleRemoveDuoClick = () => {
    removeDuoMutation.mutate({ userId });
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className={triggerClassName}>
            <EllipsisVertical className="size-6" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent side="left" align="start" className="w-56">
          <DropdownMenuItem variant="destructive" onClick={handleReportClick}>
            <FlagTriangleRight />
            <span>Report & Block</span>
          </DropdownMenuItem>
          {isDuo && (
            <DropdownMenuItem
              variant="destructive"
              onClick={handleRemoveDuoClick}
              disabled={removeDuoMutation.isPending}
            >
              <X />
              <span>Remove duo</span>
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      <ReportModal
        open={isReportModalOpen}
        onOpenChange={setIsReportModalOpen}
        userId={userId}
        redirectTo="/my-duos"
      />
    </>
  );
});
